import React from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useSearchParams } from "react-router";
import { Pagination } from "@sscs/types";
import { DataTable, Page, hasActiveFilters } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { ORG_ROLES } from "Constants";
import {
  useCheckPaginationPage,
  useCurrentOrganization,
  useFilterSortPaginationWithUrl,
} from "Hooks";
import MemberRow from "../../components/MemberRow/MemberRow";
import MembersTableActionBar from "../../components/MembersTableActionBar/MembersTableActionBar";
import { useMembersColumns } from "../../components/MembersTableHeader/MembersTableHeader";
import { useGetUsersListQuery } from "../../services/users.api";
import { FilterMembers, SortMembers } from "../../types";

const initSort: SortMembers = {
  property: "email",
  reverse: false,
};

// The at-rest filter state the found summary compares against and the reset
// link restores — intentionally NOT `initFilter`, which may carry filters
// pre-applied via the URL's search params.
const atRestFilter: FilterMembers = {
  email: "",
  role: "ALL",
};

const initPagination: Pagination = {
  currentPage: 1,
  itemsPerPage: 10,
};

const MembersTable = (): React.JSX.Element => {
  const [searchParams] = useSearchParams();
  const currentOrganization = useCurrentOrganization();

  const searchParamRole = searchParams.get("role");
  const initFilter: FilterMembers = {
    email: searchParams.get("email") ?? "",
    role:
      searchParamRole && ORG_ROLES.some((role) => role === searchParamRole)
        ? searchParamRole
        : "ALL",
  };

  const {
    filter,
    sort,
    pagination,
    handleFilterChange,
    handleSortChange,
    handlePaginationChange,
  } = useFilterSortPaginationWithUrl(initFilter, initSort, initPagination);

  const { columns, permissions } = useMembersColumns();

  const { data, isError, isFetching, isLoading, error } = useGetUsersListQuery(
    currentOrganization
      ? {
          ...filter,
          organization: currentOrganization.id,
          sortBy: `${sort.reverse ? "-" : ""}${sort.property}`,
          offset: (pagination.currentPage - 1) * pagination.itemsPerPage,
          limit: pagination.itemsPerPage,
        }
      : skipToken,
  );

  useCheckPaginationPage({
    matchCount: data?.match_count,
    itemLength: data?.users.length,
    currentPage: pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    handlePaginationChange,
  });

  return (
    <>
      <Page.Filters
        found={{
          id: "members-clear-filters",
          count: data?.match_count ?? 0,
          initialFilters: atRestFilter,
          activeFilters: filter,
          onClear: () => handleFilterChange(atRestFilter),
        }}
      >
        <MembersTableActionBar
          filter={filter}
          onFilterChange={handleFilterChange}
        />
      </Page.Filters>
      <Page.Body>
        <DataTable
          columns={columns}
          sort={sort}
          onSortChange={handleSortChange}
          empty={{
            subject: "users",
            filters: {
              active: hasActiveFilters(atRestFilter, filter),
              onReset: () => handleFilterChange(atRestFilter),
            },
          }}
          pagination={{
            itemCount: data?.match_count ?? 0,
            pagination,
            onChange: handlePaginationChange,
          }}
          status={getQueryStatus({
            isLoading,
            isFetching,
            isError,
            error,
            data,
          })}
        >
          {data?.users.map((member) => (
            <MemberRow
              key={member.user_id}
              member={member}
              permissions={permissions}
            />
          ))}
        </DataTable>
      </Page.Body>
    </>
  );
};

export default MembersTable;
